import {
  Box,
  Button,
  DiscoveryIndicator,
  Icon,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Separator,
  Text,
  Toggle,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type Color,
  type IconName,
} from "@salestrak/ui";

import type { WidgetViewLens } from "#app/widgets/dashboards/widget-view-mode";
import type {
  WidgetViewTier,
  WidgetViewWriteGrant,
} from "#app/widgets/dashboards/widget-view-policy";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import type { IssueScope } from "#app/lib/filters/issue-scope";
import type { Omittable } from "@salestrak/ui";

/**
 * The ownership pill — ONE pill, toolbar-left in every toolbar variant,
 * naming the viewer's relationship to this view. ONE configurable component
 * for every view and every viewer: the states below are driven entirely by
 * data (`tier`, `canWrite`, fork/lens, `dirty`), never by viewer-specific
 * code paths, so a workflow dashboard, a team overview, and My Work all
 * present the same UI/UX.
 *
 * The pill carries every state's explanation AND its action (the Personal ↔
 * Shared lens switch, Reset, Discard/Save for pending shared edits, My
 * Work's reset-to-default) instead of a separate banner or toolbar button.
 * A pure info state stays a hover Tooltip; a state with an action needs a
 * click-triggered Popover so the buttons are reachable.
 *
 * The pill names WHICH BOARD the viewer is looking at — "Shared" or
 * "Personalized" (plus My Work's "Private") — never a permission state.
 * Whether the viewer can edit the shared board (and why or why not) is part
 * of the Shared state's copy, not a separate "View only" look.
 *
 * Every shared view's popover ALWAYS shows the Personal ↔ Shared switch —
 * it never collapses to a one-sided "Personalize" or "back to shared"
 * affordance. With no fork yet, selecting "Personal" IS the Personalize
 * entry (the host arms the fork); the hint line below the description
 * explains that before the first press.
 *
 * | viewer state                          | pill          | popover content             |
 * |---------------------------------------|---------------|-----------------------------|
 * | private-owner, no default to reset to | Private       | tooltip only                |
 * | private-owner + onResetToDefault      | Private       | Reset to default layout     |
 * | shared board, no fork                 | Shared        | can/can't-edit copy + switch (Personal personalizes) |
 * | personal lens active (anyone)         | Personalized  | lens switch, Reset          |
 * | shared lens, fork exists              | Shared        | can/can't-edit copy + lens switch |
 * | dirty template (writers only), no fork| Shared + dot  | SAVE DESTINATION panel:     |
 * |                                       |               | team / just-me, Discard (+ switch, Personal disabled) |
 * | dirty template, fork exists           | Shared + dot  | Discard / Save (+ switch)   |
 *
 * The popover reads top-down as header → control → footer (`padding="none"`,
 * sectioned against the `bg-popover` surface): a title + muted description
 * anchor the state, the lens switch is a real segmented control
 * (`ToggleGroup variant="outline"` — joined borders, group-owned selection, never
 * two free-floating chips), and actions sit in a hairline-separated footer
 * row so they read as commands, not copy.
 *
 * "Personal" disables in the lens switch while staged template edits are
 * dirty: Save/Discard sit in the footer right below, so no unsaved-edit
 * prompt is ever needed.
 */

/**
 * The popover header's anchor: a soft-tinted square holding the state icon,
 * beside the state title. Written out here so the header keeps the three type
 * strengths it depends on.
 */
function PillHeaderAnchor({
  icon,
  color,
  name,
}: Omittable<{
  icon: IconName;
  color?: Color;
  name: string;
}>) {
  return (
    <Box direction="row" align="center" gap="xs">
      <Box
        padding="xs"
        rounded="sm"
        className="bg-c-soft"
        data-color={color ?? "muted"}
      >
        <Icon name={icon} size="md" className="text-c" />
      </Box>
      <Box grow shrink>
        <Text size="sm" weight="medium" truncate={1}>
          {name}
        </Text>
      </Box>
    </Box>
  );
}
export function OwnershipPill({
  tier,
  canWrite,
  scopeKind,
  writeGrant,
  personalActive,
  dirty,
  stagedAudience,
  lens,
  onSwitchLens,
  onSaveAsPersonal,
  onResetPersonal,
  onDiscardStaged,
  onSaveForTeam,
  onResetToDefault,
  hasFork,
}: Omittable<{
  tier: WidgetViewTier;
  /** Whether this viewer may edit the SHARED board (server-accepted writes). */
  canWrite: boolean;
  /** The host scope's kind — names the shared audience and picks the entity icon. */
  scopeKind: IssueScope["kind"];
  /** WHY the viewer can edit the shared layout — drives the "because you
   *  lead this team / can manage widgets" line. Null for non-editors. */
  writeGrant: WidgetViewWriteGrant | null;
  /** The viewer is on their personal layer (fork exists or Personalize armed). */
  personalActive: boolean;
  /** Whether the viewer's personal fork exists yet — with no fork the lens
   *  switch's "Personal" side reads as the Personalize entry, so the hint
   *  line explains the concept before the first press. */
  hasFork: boolean;
  /** Staged template edits the audience can't see yet (writers only). */
  dirty: boolean;
  /** Who a saved shared edit reaches — copy computed by the host. */
  stagedAudience: string;
  /** Personal ↔ Shared lens switch (EVERY shared-tier viewer): the effective
   *  lens + handler. Forkless viewers get it too — the host's handler treats
   *  a forkless "Personal" select as the Personalize entry. Omit both only on
   *  private-owner views (no shared board to switch to). */
  lens?: WidgetViewLens;
  onSwitchLens?: (lens: WidgetViewLens) => void;
  /** "Save for just me": staged edits become the viewer's personal fork
   *  instead of the shared board. Pass only for writers with NO existing
   *  fork — the dirty popover then offers BOTH destinations so the choice is
   *  made AFTER editing, never by which lens happened to be active before. */
  onSaveAsPersonal?: () => void;
  onResetPersonal: () => void;
  onDiscardStaged: () => void;
  onSaveForTeam: () => void;
  /** Private-owner views with a designed default (My Work): restore the
   *  default seeded layout. Omitted → the private pill stays a tooltip. */
  onResetToDefault?: () => void;
}>) {
  const sharedScopeNoun = scopeKind === "workflow" ? "workflow" : "team";
  const scopeIcon = scopeKind === "workflow" ? ENTITY_KIND_ICON.workflow : ENTITY_KIND_ICON.team;
  // A workflow dashboard's shared audience is everyone who can see it, not a
  // team — the save action names the audience it publishes to.
  const saveSharedLabel = scopeKind === "workflow" ? "Save for everyone" : "Save for the team";
  // WHO can edit the shared layout / WHY this viewer can: the popover's job
  // is to answer both, in the viewer's own terms.
  const sharedEditorsNoun =
    scopeKind === "team" ? "this team's leads and admins" : "admins";
  const editReason =
    writeGrant === "team-lead" ? "you lead this team" : "you can manage widgets";
  const isSharedEditor = tier === "shared" && canWrite;
  const pillProps =
    tier === "private-owner"
      ? {
          icon: "lock" as const,
          label: "Private",
          title: "Private layout",
          description: onResetToDefault
            ? "Only you can see or edit this layout. You can always reset it back to the default below."
            : "Only you can see or edit this layout.",
        }
      : personalActive
        ? {
            icon: "sliders-horizontal" as const,
            label: "Personalized",
            title: "Your personalized version",
            description: onSwitchLens
              ? `Only you see this version. The ${sharedScopeNoun}'s shared layout is unchanged — switch back to it below.`
              : `Only you see this version. The ${sharedScopeNoun}'s shared layout is unchanged.`,
          }
        : {
            icon: scopeIcon,
            label: "Shared",
            title: "Shared layout",
            description: canWrite
              ? `Everyone who can see this ${sharedScopeNoun} sees this layout. You can edit it because ${editReason}.`
              : `Everyone who can see this ${sharedScopeNoun} sees this layout. Only ${sharedEditorsNoun} can edit it.`,
          };

  // Unsaved edits with BOTH destinations available (a writer with no fork
  // yet): a decision panel of stacked full-width buttons in the body — the
  // choice of where the edits land is made AFTER editing, deterministically,
  // never by which lens happened to be active before the edit.
  const hasDestinationChoice = isSharedEditor && dirty && !!onSaveAsPersonal;
  const destinationSection = hasDestinationChoice ? (
    <Box direction="column" gap="xs">
      <Button size="sm" className="w-full" onClick={onSaveForTeam}>
        <Icon name="check" size="sm" />
        <Text>{saveSharedLabel}</Text>
      </Button>
      <Button variant="outline" size="sm" className="w-full" onClick={onSaveAsPersonal}>
        <Icon name="sliders-horizontal" size="sm" />
        <Text>Save for just me</Text>
      </Button>
    </Box>
  ) : null;

  // The footer's command row — ONE state's actions at a time: the personal
  // layer's Reset, the dirty template's Discard (+ Save pair when no
  // destination panel took it), or the private view's reset-to-default.
  // Personalize has NO footer row: the lens switch above is the one entry.
  const footerActions = personalActive ? (
    <Button variant="ghost" size="xs" onClick={onResetPersonal}>
      <Icon name="rotate-ccw" size="sm" />
      <Text>Reset to shared layout</Text>
    </Button>
  ) : hasDestinationChoice ? (
    <Button variant="ghost" size="xs" onClick={onDiscardStaged}>
      <Text>Discard changes</Text>
    </Button>
  ) : isSharedEditor && dirty ? (
    <>
      <Button variant="ghost" size="xs" onClick={onDiscardStaged}>
        <Text>Discard</Text>
      </Button>
      <Button variant="secondary" size="xs" onClick={onSaveForTeam}>
        <Icon name="check" size="sm" />
        <Text>{saveSharedLabel}</Text>
      </Button>
    </>
  ) : onResetToDefault ? (
    <Button variant="ghost" size="xs" onClick={onResetToDefault}>
      <Icon name="rotate-ccw" size="sm" />
      <Text>Reset to default layout</Text>
    </Button>
  ) : null;

  // The Personal ↔ Shared board switch: a real segmented control — the group
  // owns the selection, borders join. Switching to the personal fork is
  // blocked while staged edits are dirty — Save or Discard sit in the footer
  // right below.
  const lensSection =
    onSwitchLens && lens ? (
      <Box direction="column" gap="2xs">
        <Text size="xs" variant="muted" weight="medium">
          Viewing
        </Text>
        {/* `ToggleGroup variant="outline"` is the segmented control: the group
          * owns selection and joins the borders. A single-entry value array is
          * Base UI's single-select shape (`multiple` defaults false).
          * Deselecting the active side emits an empty array, which the handler
          * below drops — so one side is always selected. */}
        <ToggleGroup
          size="xs"
          variant="outline"
          value={[lens]}
          onValueChange={(next) => {
            const picked = next[0] as WidgetViewLens | undefined;
            if (picked && picked !== lens) onSwitchLens(picked);
          }}
        >
          <ToggleGroupItem value="personal" disabled={dirty}>
            <Icon name="sliders-horizontal" size="sm" />
            <Text>Personal</Text>
          </ToggleGroupItem>
          <ToggleGroupItem value="template">
            <Icon name={scopeIcon} size="sm" />
            <Text>Shared</Text>
          </ToggleGroupItem>
        </ToggleGroup>
      </Box>
    ) : null;

  // Header: a padded icon anchor + semibold title, then muted detail — the
  // panel must read as three distinct strengths (anchor → title → detail),
  // never a uniform gray block. A dirty template takes over the header —
  // "Unsaved changes" on a warning-tinted anchor plus, when both
  // destinations are on offer, one line that frames the decision the
  // buttons below make. Only urgency gets color: the personalized state's
  // violet, the dirty state's warning; everything else stays a muted square.
  const headerTitle = isSharedEditor && dirty ? "Unsaved changes" : pillProps.title;
  const headerIcon = isSharedEditor && dirty ? ("triangle-alert" as const) : pillProps.icon;
  const headerColor =
    isSharedEditor && dirty
      ? ("warning" as const)
      : personalActive
        ? ("violet" as const)
        : undefined;
  const headerDescription =
    isSharedEditor && dirty ? (
      hasDestinationChoice ? (
        <Text size="xs">
          Save them to the shared layout —{" "}
          <Text size="xs" weight="medium">
            {stagedAudience}
          </Text>{" "}
          will see it — or keep them as a personalized version only you see.
        </Text>
      ) : (
        <Text size="xs">
          You’ve changed the shared board —{" "}
          <Text size="xs" weight="medium">
            {stagedAudience}
          </Text>{" "}
          will see this once saved.
        </Text>
      )
    ) : (
      <Text size="xs" variant="muted">
        {pillProps.description}
      </Text>
    );

  // The switch's forkless "Personal" side needs its concept explained BEFORE
  // it is pressed — a viewer who has never forked has no reason to know what
  // a personalized version is. Writers hear it as an "also" alongside their
  // template editing; read-only viewers hear it as their way in. Once a fork
  // exists (or while a dirty template owns the popover) the hint retires.
  const personalizeHint =
    tier === "shared" && !hasFork && !personalActive && !dirty && onSwitchLens ? (
      <Text size="xs" variant="muted">
        {canWrite
          ? "Switching to Personal keeps a personalized version that only you see — the shared layout stays untouched."
          : "Switching to Personal gives you your own version of this layout that only you see — the shared layout stays untouched."}
      </Text>
    ) : null;

  // Same trigger chip the data-table group-by control uses: an always-expanded
  // chip whose icon face and label are both always visible.
  const pillBadge = (
    <Toggle size="xs" variant="outline" pressed>
      <Icon name={pillProps.icon} size="sm" />
      <Text>{pillProps.label}</Text>
    </Toggle>
  );

  // Unsaved staged edits pulse a dot on the pill — the pill IS the save
  // entry point, so it has to visibly call for the click.
  const hasUnsavedShared = isSharedEditor && dirty;

  return footerActions || lensSection ? (
    <DiscoveryIndicator visible={hasUnsavedShared} color="warning" placement="top-right">
      <Popover>
        <PopoverTrigger render={pillBadge} />
        {/* The panel is sectioned edge-to-edge against the popover surface. */}
        <PopoverPopup side="bottom" align="start" padding="none">
          {/* Zone 1: identity — anchored title over receding muted detail. */}
          <Box padding="md" gap="md">
            <Box direction="column" gap="xs">
              <PillHeaderAnchor
                icon={headerIcon}
                color={headerColor}
                name={headerTitle}
              />
              {headerDescription}
              {personalizeHint}
            </Box>
            {/* Zone 2: the control — destination decision or lens switch. */}
            {destinationSection}
            {lensSection}
          </Box>
          {/* Zone 3: quiet commands, separated by a rule rather than a fill —
              the popover is one surface from edge to edge. */}
          {footerActions ? (
            <>
              <Separator />
              <Box
                direction="row"
                align="center"
                justify="end"
                gap="xs"
                paddingX="sm"
                paddingY="xs"
              >
                {footerActions}
              </Box>
            </>
          ) : null}
        </PopoverPopup>
      </Popover>
    </DiscoveryIndicator>
  ) : (
    <Tooltip>
      <TooltipTrigger render={pillBadge} />
      <TooltipPopup side="bottom">
        <Text size="xs">{pillProps.description}</Text>
      </TooltipPopup>
    </Tooltip>
  );
}
